import { action, observable } from 'mobx';
import { SetupIntent, SubscriptionStatus } from '../models';
import {
  default as getRepository,
  SubscriptionRepository
} from '../repositories/Subscription';
import AsyncOperation from './AsyncOperation';
import { AuthStore, RouterStore } from './';

export type SetupCardFunction = (clientSecret: string) => Promise<string>;

export class SubscriptionStore {
  public repository: SubscriptionRepository = getRepository();

  @observable
  public videoPopupShowing: boolean = false;

  @observable
  public subscribed: AsyncOperation<boolean> = new AsyncOperation(
    Promise.resolve(false)
  );

  @observable
  public cancelling: AsyncOperation<void> = new AsyncOperation(
    Promise.resolve()
  );

  @observable
  public cardUpdate: AsyncOperation<boolean> = new AsyncOperation(
    Promise.resolve(false)
  );

  @observable
  public originalUrl?: string;

  constructor(public routerStore: RouterStore, public authStore: AuthStore) {}

  @action
  public showVideoPopup(): void {
    this.videoPopupShowing = true;
  }

  @action
  public closeVideoPopup(): void {
    this.videoPopupShowing = false;
  }

  /**
   * Takes the user to the sign-up page, and captures their current page url
   * as `store.originalUrl` - which can be used to provide a link back to their
   * starting point.
   */
  @action
  public requestSignUp(): void {
    const {
      routerStore: {
        history,
        location: { pathname, search }
      }
    } = this;

    this.originalUrl = `${pathname}${search}`;

    history.push(`/premier`);
  }

  /**
   * Creates a setup intent via the API - required to begin performing SCA.
   */
  @action
  public async createSetupIntent(): Promise<SetupIntent> {
    return this.repository.createSetupIntent();
  }

  /**
   * After a successful subscription, this updates the user's subscribed
   * state in the store.
   */
  @action
  public subscribe(
    planId: string,
    setupCard: SetupCardFunction
  ): Promise<boolean> {
    this.subscribed = new AsyncOperation(this._subscribe(planId, setupCard));

    return this.subscribed.originalPromise;
  }

  @action
  public updateCard(
    setupCard: SetupCardFunction
  ): Promise<boolean | undefined> {
    this.cardUpdate = new AsyncOperation(this._updateCard(setupCard));

    return this.cardUpdate.originalPromise;
  }

  @action
  public cancelMembership(): Promise<void> {
    this.cancelling = new AsyncOperation(this._cancelMembership());

    return this.cancelling.originalPromise;
  }

  /**
   * Abandons the current subscription flow and clears all related data from
   * the store.
   */
  @action
  public abandonSubscriptionFlow() {
    this.originalUrl = undefined;
    this.subscribed = new AsyncOperation(Promise.resolve(false));
  }

  private _subscribe = async (
    planId: string,
    setupCard: SetupCardFunction
  ): Promise<boolean> => {
    const setupIntent = await this.createSetupIntent();

    const paymentMethodId = await setupCard(setupIntent.clientSecret);

    await this.repository.subscribe(planId, paymentMethodId);

    // Make sure we locally update  the current user's premium to 'active'
    this.authStore.setCurrentUserPremiumStatus(SubscriptionStatus.Active);

    return true;
  };

  private _updateCard = async (setupCard: SetupCardFunction) => {
    const setupIntent = await this.createSetupIntent();

    const paymentMethodId = await setupCard(setupIntent.clientSecret);

    await this.repository.updateCard(paymentMethodId);

    // Make sure we locally update  the current user's premium to 'active' (?)
    this.authStore.setCurrentUserPremiumStatus(SubscriptionStatus.Active);

    return true;
  };

  private _cancelMembership = async () => {
    const endsAt = await this.repository.cancelMembership();

    this.authStore.setPremiumEndsAt(endsAt);
  };
}

export default SubscriptionStore;



// WEBPACK FOOTER //
// ./src/app/stores/SubscriptionStore.ts